import { useState, useEffect, useCallback } from "react";
import Percentile from "./Percentile";
import Tippy from "@tippyjs/react";

import { UserIcon } from "@heroicons/react/solid";

function SubScale(props) {
    const {
        userName,
        name,
        percentile,
        colour,
        lowAdjective,
        highAdjective,
        description,
        band_description,
        colourPalette,
        scaleIndex,
    } = props;

    const [position, setPosition] = useState(0);

    const [textColour, setTextColour] = useState("text-blue-gray-50");

    const [lowPercentile, setLowPercentile] = useState(false);
    const [mediumPercentile, setMediumPercentile] = useState(false);
    const [highPercentile, setHighPercentile] = useState(false);

    const handleSetColour = useCallback(
        async (percentileNumber) => {
            if (percentileNumber <= 33) {
                setTimeout(() => {
                    setTextColour(
                        colourPalette[scaleIndex]
                            ? `text-${colour}-${colourPalette[scaleIndex]}`
                            : `text-${colour}-${colourPalette[colourPalette.length - 1]}`
                    );
                    setLowPercentile(true);
                }, 100);
            } else if (percentileNumber < 74) {
                setTimeout(() => {
                    setTextColour(
                        colourPalette[scaleIndex]
                            ? `text-${colour}-${colourPalette[scaleIndex]}`
                            : `text-${colour}-${colourPalette[colourPalette.length - 1]}`
                    );
                    setMediumPercentile(true);
                }, 100);
            } else {
                setTimeout(() => {
                    setTextColour(
                        colourPalette[scaleIndex]
                            ? `text-${colour}-${colourPalette[scaleIndex]}`
                            : `text-${colour}-${colourPalette[colourPalette.length - 1]}`
                    );
                    setHighPercentile(true);
                }, 100);
            }
        },
        [colour, colourPalette, scaleIndex]
    );

    useEffect(() => {
        setTimeout(() => {
            setPosition(percentile);
        }, 100);
        handleSetColour(percentile);
    }, [handleSetColour, percentile]);

    return (
        <div>
            {description ? (
                <>
                    <Tippy
                        content={description}
                        className={"p-2 shadow rounded-md bg-secondary-500 text-white text-sm"}
                        arrow={false}
                        animation={"shift-away-subtle"}
                    >
                        <h2
                            className={
                                "cursor-help transition transition-color duration-4000 ease-in-out font-medium text-lg " +
                                textColour
                            }
                        >
                            {name}
                        </h2>
                    </Tippy>

                    <p className="text-blue-gray-500 my-3 text-md">{description}</p>
                </>
            ) : (
                <h2
                    className={
                        "transition transition-color duration-4000 ease-in-out font-medium text-lg " + textColour
                    }
                >
                    {name}
                </h2>
            )}

            <div className="flex flex-col-reverse lg:flex-row gap-4 mt-3">
                <div className={"w-full"}>
                    <div className={"w-full bg-gradient-to-r from-blue-gray-50 to-primary-50 p-4 rounded-lg"}>
                        <div>
                            <div className="relative w-full bg-slate-200 rounded-lg h-6 mt-2 overflow-hidden">
                                <div
                                    className={`rounded-lg h-6 border-2 border-white overflow-hidden absolute ${
                                        highPercentile === true
                                            ? colourPalette[scaleIndex]
                                                ? `bg-${colour}-${colourPalette[scaleIndex]}`
                                                : `bg-${colour}-${colourPalette[colourPalette.length - 1]}`
                                            : `bg-slate-400`
                                    }`}
                                    style={{ width: "100%" }}
                                />
                                <div
                                    className={`rounded-l-lg h-6 border-2 border-white overflow-hidden absolute ${
                                        mediumPercentile === true
                                            ? colourPalette[scaleIndex]
                                                ? `bg-${colour}-${colourPalette[scaleIndex]}`
                                                : `bg-${colour}-${colourPalette[colourPalette.length - 1]}`
                                            : `bg-slate-300`
                                    }`}
                                    style={{ width: "73%" }}
                                />
                                <div
                                    className={`rounded-l-lg h-6 border-2 border-white overflow-hidden absolute ${
                                        lowPercentile === true
                                            ? colourPalette[scaleIndex]
                                                ? `bg-${colour}-${colourPalette[scaleIndex]}`
                                                : `bg-${colour}-${colourPalette[colourPalette.length - 1]}`
                                            : `bg-slate-200`
                                    }`}
                                    style={{ width: "33%" }}
                                />
                            </div>

                            <div className="flex items-center justify-between text-sm font-medium text-blue-gray-500 mt-2">
                                <span className={"text-blue-gray-500"}>{lowAdjective}</span>
                                <span className={"text-blue-gray-700"}>{highAdjective}</span>
                            </div>
                            <div
                                className={
                                    "relative transition transition-width duration-4000 w-" + position + "-percentage"
                                }
                            >
                                <div className={"absolute -top-16 -right-0"}>
                                    <div
                                        className={
                                            "flex flex-row gap-1 justify-center items-center text-amber-700 bg-amber-200 " +
                                            (position === 0 ? "opacity-0 " : "opacity-1 ") +
                                            "w-max p-1 rounded-full rounded-br-none transition transition-opacity duration-4000 z-0"
                                        }
                                    >
                                        <UserIcon className="block h-3.5 w-3.5" />
                                        <span className={"text-xs"}>{userName}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {band_description && <p className={"text-blue-gray-500 mt-2"}>{band_description}</p>}
                    </div>
                </div>
                <div className={"flex-grow flex items-center justify-center"}>
                    <Percentile
                        size={"large"}
                        percentile={percentile}
                        colour={colour}
                        colourPalette={colourPalette}
                        scaleIndex={scaleIndex}
                    />
                </div>
            </div>
        </div>
    );
}

export default SubScale;
